import React, { useState, useEffect } from 'react';
import { Tabs, Tab, Button, Spinner, Form, Alert, Table, Badge } from 'react-bootstrap';
import CustomModal from '../CustomModal/CustomModal';
import CustomFilterTable from '../CustomFilterTable/CustomFilterTable';
import CustomTable from '../CustomTable/CustomTable';
import useUtenti from '../../hooks/useUtenti';
import useCorsiUtenti from '../../hooks/useCorsiUtenti';
import CorsiUtentiService from '../../services/CorsiUtentiService/CorsiUtentiService';
import { alertService } from '../../services/AlertService';
import { ROLES } from '../../model/roles';
import { useSelector } from 'react-redux';
import useUserConfigTable from '../../configTable/useUserConfigTable';

const AssegnaVoucherAgenziaModal = ({ voucher, idAgenzia, show, onHide, onSuccess }) => {
    const userLoggedIn = useSelector(state => state.user?.userData);
    const { getUtenteByParams, createUtente, loadingStateUtenti } = useUtenti();
    const { assignCourseToUser } = useCorsiUtenti();
    
    const [activeTab, setActiveTab] = useState('esistente');
    const [utenti, setUtenti] = useState([]);
    const [utentiFiltrati, setUtentiFiltrati] = useState([]);
    const [selectedUtente, setSelectedUtente] = useState(null);
    const [formDataFilter, setFormDataFilter] = useState({});
    const [isLoadingUtenti, setIsLoadingUtenti] = useState(false);
    const [isAssigning, setIsAssigning] = useState(false);
    const [isCheckingEnrollments, setIsCheckingEnrollments] = useState(false);
    
    // Form per nuovo utente
    const [formNewUser, setFormNewUser] = useState({
        nome: '',
        cognome: '',
        email: ''
    });
    const [errors, setErrors] = useState({});

    const userConfigTable = useUserConfigTable();

    useEffect(() => {
        if (show && activeTab === 'esistente') {
            // Carica utenti dell'agenzia al caricamento
            loadUtenti();
        }
    }, [show, activeTab]);

    const loadUtenti = async () => {
        setIsLoadingUtenti(true);
        setIsCheckingEnrollments(true);
        try {
            const params = {
                filtri: {
                    id_agenzia: idAgenzia,
                    tipo_utente: 'corsista',
                    attivo: 1,
                    ...formDataFilter
                },
                limite: 1000, // Limite alto per recuperare tutti gli utenti
                pagina: 1
            };
            const response = await getUtenteByParams(params);
            // La risposta potrebbe essere { data: [...], count: ... } o direttamente un array
            const utentiData = response?.data || (Array.isArray(response) ? response : []);
            setUtenti(utentiData);
            
            // Verifica lo stato di iscrizione per ogni utente e filtra
            const utentiEleggibili = await filterEligibleUsers(utentiData);
            setUtentiFiltrati(utentiEleggibili);
        } catch (error) {
            alertService.error("Errore nel caricamento degli utenti");
        } finally {
            setIsLoadingUtenti(false);
            setIsCheckingEnrollments(false);
        }
    };

    const filterEligibleUsers = async (utentiList) => {
        const eleggibili = [];
        
        try {
            // Ottimizzazione: una sola chiamata per ottenere TUTTI gli utenti iscritti al corso con stato_pagamento
            const tuttiUtentiIscritti = await CorsiUtentiService.getCorsoUtentiByIdCorso({
                id_corso: voucher.id_corso
            });
            
            // DEBUG: Verifica cosa arriva dalla chiamata
            console.log('DEBUG - Utenti iscritti ricevuti:', tuttiUtentiIscritti);
            console.log('DEBUG - Tipo:', typeof tuttiUtentiIscritti, 'Is Array:', Array.isArray(tuttiUtentiIscritti));
            if (Array.isArray(tuttiUtentiIscritti) && tuttiUtentiIscritti.length > 0) {
                console.log('DEBUG - Primo utente:', tuttiUtentiIscritti[0]);
                console.log('DEBUG - Primo utente stato_pagamento:', tuttiUtentiIscritti[0].stato_pagamento);
            }
            
            // Creo una mappa: id_utente -> stato_pagamento per lookup veloce
            const utentiIscrittiMap = new Map();
            if (Array.isArray(tuttiUtentiIscritti)) {
                tuttiUtentiIscritti.forEach(utente => {
                    // Ora il metodo restituisce anche stato_pagamento
                    utentiIscrittiMap.set(utente.id_utente, {
                        isIscritto: true,
                        stato_pagamento: utente.stato_pagamento
                    });
                });
            }
            console.log('DEBUG - Mappa creata con', utentiIscrittiMap.size, 'utenti');
            
            // Per ogni utente nella lista, verifica se è eleggibile
            console.log('DEBUG - Verifica eleggibilità per', utentiList.length, 'utenti');
            for (const utente of utentiList) {
                const infoIscrizione = utentiIscrittiMap.get(utente.id_utente);
                
                if (!infoIscrizione) {
                    // Non iscritto al corso - eleggibile
                    eleggibili.push(utente);
                } else {
                    // È iscritto - verifica lo stato_pagamento
                    const statoPagamento = infoIscrizione.stato_pagamento;
                    console.log(`DEBUG - Utente ${utente.id_utente} (${utente.nome} ${utente.cognome}): iscritto con stato "${statoPagamento}"`);
                    // REGOLA: Solo se lo stato è "Non pagato" o "Non_pagato" l'utente è eleggibile
                    if (statoPagamento === 'Non pagato' || statoPagamento === 'Non_pagato') {
                        eleggibili.push(utente);
                        console.log(`DEBUG - Utente ${utente.id_utente} ELEGGIBILE (Non pagato)`);
                    } else {
                        console.log(`DEBUG - Utente ${utente.id_utente} NON eleggibile (stato: ${statoPagamento})`);
                    }
                    // Se è "Pagato" o "Parzialmente pagato" - NON eleggibile (non viene aggiunto)
                }
            }
            console.log('DEBUG - Totale eleggibili:', eleggibili.length);
        } catch (error) {
            // Se fallisce la chiamata principale, consideriamo tutti gli utenti come non iscritti (eleggibili)
            // Questo è un fallback per non bloccare completamente la funzionalità
            return utentiList;
        }
        
        return eleggibili;
    };

    const handleFilter = async () => {
        // Quando si applica un filtro, ricarica gli utenti e riapplica il filtro eleggibilità
        await loadUtenti();
    };

    const validateNewUser = () => {
        const newErrors = {};
        if (!formNewUser.nome || formNewUser.nome.trim() === '') {
            newErrors.nome = 'Il nome è obbligatorio';
        }
        if (!formNewUser.cognome || formNewUser.cognome.trim() === '') {
            newErrors.cognome = 'Il cognome è obbligatorio';
        }
        if (!formNewUser.email || formNewUser.email.trim() === '') {
            newErrors.email = 'L\'email è obbligatoria';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formNewUser.email)) {
            newErrors.email = 'L\'email non è valida';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleAssignToExistingUser = async () => {
        if (!selectedUtente) {
            alertService.warn("Seleziona un utente", { autoClose: true });
            return;
        }

        // Verifica che l'utente non sia già iscritto con pagamento
        try {
            const corsiUtente = await CorsiUtentiService.getCorsoUtentiByIdCorso({
                id_corso: voucher.id_corso,
                id_utente: selectedUtente.id_utente
            });

            if (Array.isArray(corsiUtente) && corsiUtente.length > 0) {
                const corsoUtente = corsiUtente[0];
                if (corsoUtente.stato_pagamento === 'Pagato' || corsoUtente.stato_pagamento === 'Parzialmente pagato' || corsoUtente.stato_pagamento === 'Parzialmente_pagato') {
                    alertService.error("L'utente è già iscritto a questo corso ed è stato registrato un pagamento. Il voucher non può essere utilizzato.", { autoClose: false });
                    return;
                }
            }
        } catch (error) {
            // Errore nella verifica iscrizione - ignora
        }

        setIsAssigning(true);
        try {
            const data = {
                id_utente: selectedUtente.id_utente,
                id_utenti_voucher: voucher.id_utenti_voucher
            };

            const response = await CorsiUtentiService.assegnaVoucherACorsista(data);
            if (response && response.success) {
                alertService.success(`Voucher assegnato con successo! L'utente è stato iscritto al corso "${voucher.nome_corso}"`, { autoClose: true });
                if (onSuccess) {
                    onSuccess();
                }
                onHide();
            } else {
                let errorMessage = "Errore durante l'assegnazione del voucher";
                if (response?.error?.message) {
                    errorMessage = response.error.message;
                    if (errorMessage.includes('Errore di validazione dei campi : ')) {
                        errorMessage = errorMessage.replace('Errore di validazione dei campi : ', '');
                    }
                }
                alertService.error(errorMessage, { autoClose: false });
            }
        } catch (error) {
            let errorMessage = "Errore durante l'assegnazione del voucher";
            if (error?.error?.message) {
                errorMessage = error.error.message;
                if (errorMessage.includes('Errore di validazione dei campi : ')) {
                    errorMessage = errorMessage.replace('Errore di validazione dei campi : ', '');
                }
            } else if (error?.response?.data?.error?.message) {
                errorMessage = error.response.data.error.message;
            }
            alertService.error(errorMessage, { autoClose: false });
        } finally {
            setIsAssigning(false);
        }
    };

    const handleCreateAndAssign = async () => {
        if (!validateNewUser()) {
            return;
        }

        // Verifica che l'email non esista già
        try {
            const existingUsers = await getUtenteByParams({ username: formNewUser.email });
            if (Array.isArray(existingUsers) && existingUsers.length > 0) {
                setErrors({ email: 'Questa email è già registrata nel sistema' });
                return;
            }
        } catch (error) {
            // Se l'utente non esiste, procediamo
        }

        setIsAssigning(true);
        try {
            // Crea l'utente
            const userData = {
                nome: formNewUser.nome.trim(),
                cognome: formNewUser.cognome.trim(),
                username: formNewUser.email.trim(),
                tipo_utente: 'corsista',
                id_agenzia: idAgenzia
            };

            const newUser = await createUtente(userData);
            
            if (newUser?.id || newUser?.id_utente) {
                const userId = newUser.id || newUser.id_utente;
                
                // Assegna il voucher (e quindi iscrive al corso)
                const data = {
                    id_utente: userId,
                    id_utenti_voucher: voucher.id_utenti_voucher
                };

                const response = await CorsiUtentiService.assegnaVoucherACorsista(data);
                if (response && response.success) {
                    alertService.success(`Utente creato e voucher assegnato con successo! L'utente è stato iscritto al corso "${voucher.nome_corso}"`, { autoClose: true });
                    if (onSuccess) {
                        onSuccess();
                    }
                    onHide();
                    // Reset form
                    setFormNewUser({ nome: '', cognome: '', email: '' });
                    setErrors({});
                } else {
                    let errorMessage = "Errore durante l'assegnazione del voucher";
                    if (response?.error?.message) {
                        errorMessage = response.error.message;
                    }
                    alertService.error(errorMessage, { autoClose: false });
                }
            } else {
                alertService.error("Errore nella creazione dell'utente", { autoClose: false });
            }
        } catch (error) {
            let errorMessage = "Errore durante la creazione dell'utente o l'assegnazione del voucher";
            if (error?.error?.message) {
                errorMessage = error.error.message;
            } else if (error?.response?.data?.error?.message) {
                errorMessage = error.response.data.error.message;
            }
            alertService.error(errorMessage, { autoClose: false });
        } finally {
            setIsAssigning(false);
        }
    };

    const searchNodeType = [
        { nodeType: "input", name: "nome", placeholder: "Nome" },
        { nodeType: "input", name: "cognome", placeholder: "Cognome" },
        { nodeType: "input", name: "username", placeholder: "Email" }
    ];

    const handleClose = () => {
        setActiveTab('esistente');
        setSelectedUtente(null);
        setUtenti([]);
        setUtentiFiltrati([]);
        setFormDataFilter({});
        setFormNewUser({ nome: '', cognome: '', email: '' });
        setErrors({});
        onHide();
    };

    return (
        <CustomModal
            show={show}
            onHide={handleClose}
            onUndoChanges={handleClose}
            headerText={`Assegna Voucher - ${voucher?.nome_corso || ''}`}
            size="xl"
            hideSaveBtn={true}
            body={
                <div>
                    <Alert variant="info" className="mb-3">
                        <i className="bi bi-info-circle me-2"></i>
                        <strong>Voucher selezionato:</strong> {voucher?.nome_corso}
                        <br />
                        <small>Quantità disponibile: <strong>{voucher?.qt_disponibile - (voucher?.qt_utilizzata || 0)}</strong> | Scadenza: {new Date(voucher?.data_scadenza).toLocaleDateString('it-IT')}</small>
                    </Alert>

                    <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-3">
                        <Tab eventKey="esistente" title={
                            <span>
                                <i className="bi bi-person-check me-1"></i>
                                Utente Esistente
                            </span>
                        }>
                            <div className="mt-3">
                                <CustomFilterTable
                                    searchNodeType={searchNodeType}
                                    setFormDataFilter={setFormDataFilter}
                                    formDataFilter={formDataFilter}
                                    onFilteredTable={handleFilter}
                                    isLoadingData={isLoadingUtenti}
                                />

                                {(isLoadingUtenti || isCheckingEnrollments) ? (
                                    <div className="text-center py-4">
                                        <Spinner animation="border" />
                                        <p className="mt-2 text-muted">
                                            {isCheckingEnrollments ? 'Verifica iscrizioni in corso...' : 'Caricamento utenti...'}
                                        </p>
                                    </div>
                                ) : (
                                    <div className="mt-3">
                                        {utentiFiltrati.length === 0 ? (
                                            <div className="text-center py-4">
                                                <p className="text-muted">
                                                    {utenti.length === 0 
                                                        ? 'Nessun utente trovato per questa agenzia.' 
                                                        : 'Nessun utente eleggibile per questo voucher. Tutti gli utenti sono già iscritti al corso con pagamento registrato.'}
                                                </p>
                                            </div>
                                        ) : (
                                            <>
                                                <p className="text-muted mb-2">
                                                    Trovati {utentiFiltrati.length} utenti eleggibili su {utenti.length} totali. 
                                                    Seleziona un utente dalla tabella:
                                                </p>
                                                <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                                    <Table striped bordered hover className="align-middle">
                                                        <thead className="table-light sticky-top">
                                                            <tr>
                                                                {userConfigTable.map((col) => (
                                                                    <th key={col.key} style={{ width: col.width || 'auto' }}>
                                                                        {col.label}
                                                                    </th>
                                                                ))}
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {utentiFiltrati.map((utente) => (
                                                                <tr 
                                                                    key={utente.id_utente}
                                                                    onClick={() => setSelectedUtente(utente)}
                                                                    style={{ 
                                                                        cursor: 'pointer',
                                                                        backgroundColor: selectedUtente?.id_utente === utente.id_utente ? '#e7f3ff' : 'inherit'
                                                                    }}
                                                                    className={selectedUtente?.id_utente === utente.id_utente ? 'table-primary' : ''}
                                                                >
                                                                    {userConfigTable.map((col) => {
                                                                        const value = col.key?.split('.')?.reduce((obj, key) => obj && obj[key], utente);
                                                                        return (
                                                                            <td key={`${utente.id_utente}-${col.key}`}>
                                                                                {col.formatter ? col.formatter(value, utente) : (value ?? 'N/A')}
                                                                            </td>
                                                                        );
                                                                    })}
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </Table>
                                                </div>
                                                {selectedUtente && (
                                                    <div className="mt-3 alert alert-info">
                                                        <strong>Utente selezionato:</strong> {selectedUtente.cognome} {selectedUtente.nome} ({selectedUtente.username})
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                )}

                                <div className="mt-3 text-end">
                                    <Button
                                        variant="primary"
                                        onClick={handleAssignToExistingUser}
                                        disabled={!selectedUtente || isAssigning}
                                    >
                                        {isAssigning ? (
                                            <>
                                                <Spinner size="sm" className="me-2" />
                                                Assegnazione in corso...
                                            </>
                                        ) : (
                                            'Assegna Voucher'
                                        )}
                                    </Button>
                                    <Button variant="secondary" className="ms-2" onClick={handleClose}>
                                        Annulla
                                    </Button>
                                </div>
                            </div>
                        </Tab>

                        <Tab eventKey="nuovo" title={
                            <span>
                                <i className="bi bi-person-plus me-1"></i>
                                Nuovo Utente
                            </span>
                        }>
                            <div className="mt-3">
                                <Form>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Nome <span className="text-danger">*</span></Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={formNewUser.nome}
                                            onChange={(e) => setFormNewUser({ ...formNewUser, nome: e.target.value })}
                                            isInvalid={!!errors.nome}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.nome}
                                        </Form.Control.Feedback>
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label>Cognome <span className="text-danger">*</span></Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={formNewUser.cognome}
                                            onChange={(e) => setFormNewUser({ ...formNewUser, cognome: e.target.value })}
                                            isInvalid={!!errors.cognome}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.cognome}
                                        </Form.Control.Feedback>
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label>Email <span className="text-danger">*</span></Form.Label>
                                        <Form.Control
                                            type="email"
                                            value={formNewUser.email}
                                            onChange={(e) => setFormNewUser({ ...formNewUser, email: e.target.value })}
                                            isInvalid={!!errors.email}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.email}
                                        </Form.Control.Feedback>
                                        <Form.Text className="text-muted">
                                            L'email sarà utilizzata come username per l'accesso al sistema
                                        </Form.Text>
                                    </Form.Group>
                                </Form>

                                <div className="mt-3 text-end">
                                    <Button
                                        variant="primary"
                                        onClick={handleCreateAndAssign}
                                        disabled={isAssigning}
                                    >
                                        {isAssigning ? (
                                            <>
                                                <Spinner size="sm" className="me-2" />
                                                Creazione in corso...
                                            </>
                                        ) : (
                                            'Crea Utente e Assegna Voucher'
                                        )}
                                    </Button>
                                    <Button variant="secondary" className="ms-2" onClick={handleClose}>
                                        Annulla
                                    </Button>
                                </div>
                            </div>
                        </Tab>
                    </Tabs>
                </div>
            }
        />
    );
};

export default AssegnaVoucherAgenziaModal;
